/**
 * Traducción humana de los `kind` de alerta a algo que entienda alguien que
 * no es developer. Es la única capa de mapping cliente: backend manda `kind`
 * + `payload.info`; el panel decide qué mostrar como título, descripción y
 * acción sugerida.
 *
 * Reglas:
 * - `label`        → título de la card (4-6 palabras, lenguaje cotidiano)
 * - `description`  → 1-2 líneas explicando qué pasó y por qué importa.
 *                    Sirve de fallback cuando `payload.info` no es humano.
 * - `suggestedAction` → qué hacer ahora. Opcional.
 *
 * Los `kind` con punto y con guion bajo (`sla.breach` / `sla_breach`) se
 * mantienen como entradas separadas para no asumir naming en el backend.
 */

export interface AlertKindMeta {
  label: string
  description: string
  suggestedAction?: string
}

const ENTRY = (m: AlertKindMeta): AlertKindMeta => m

export const ALERT_KINDS: Record<string, AlertKindMeta> = {
  'account.disconnected': ENTRY({
    label: 'Cuenta desconectada',
    description:
      'Una cuenta social perdió la conexión con Champe. Hasta que se reconecte, no podemos publicar ni moderar en esa cuenta.',
    suggestedAction: 'Reconectar la cuenta desde Cuentas → Reconectar.',
  }),
  account_disconnected: ENTRY({
    label: 'Cuenta desconectada',
    description:
      'Una cuenta social perdió la conexión con Champe. Hasta que se reconecte, no podemos publicar ni moderar en esa cuenta.',
    suggestedAction: 'Reconectar la cuenta desde Cuentas → Reconectar.',
  }),

  'token.expired': ENTRY({
    label: 'Permiso vencido',
    description:
      'El permiso que la plataforma (Meta, Instagram, LinkedIn) nos dio para operar esta cuenta caducó. Es como una sesión que expiró.',
    suggestedAction: 'Renovar el acceso desde Cuentas → Reconectar.',
  }),
  token_expired: ENTRY({
    label: 'Permiso vencido',
    description:
      'El permiso que la plataforma (Meta, Instagram, LinkedIn) nos dio para operar esta cuenta caducó. Es como una sesión que expiró.',
    suggestedAction: 'Renovar el acceso desde Cuentas → Reconectar.',
  }),

  'sla.breach': ENTRY({
    label: 'Te pasaste del tiempo de respuesta',
    description:
      'Un mensaje o comentario lleva más tiempo sin responderse del que el acuerdo con el cliente permite.',
    suggestedAction: 'Abrir Inbox y resolver el mensaje pendiente más antiguo.',
  }),
  sla_breach: ENTRY({
    label: 'Te pasaste del tiempo de respuesta',
    description:
      'Un mensaje o comentario lleva más tiempo sin responderse del que el acuerdo con el cliente permite.',
    suggestedAction: 'Abrir Inbox y resolver el mensaje pendiente más antiguo.',
  }),

  high_volume: ENTRY({
    label: 'Pico de mensajes',
    description:
      'Esta cuenta está recibiendo muchos más mensajes de lo normal. El bot puede tardar más, o necesitar refuerzo humano.',
    suggestedAction:
      'Revisar Inbox. Si el pico sigue, hacer takeover humano hasta que baje.',
  }),

  bot_confidence_low: ENTRY({
    label: 'El bot no está seguro de qué responder',
    description:
      'Llegaron mensajes que el bot no puede clasificar con seguridad. Sin revisión humana pueden responderse mal o quedar sin responder.',
    suggestedAction: 'Revisar Inbox y tomar las conversaciones marcadas en duda.',
  }),

  'kill_switch.activated': ENTRY({
    label: 'Freno de seguridad activado (cuenta)',
    description:
      'Se congeló la moderación automática de esta cuenta. El bot no responde nada hasta que se reactive.',
    suggestedAction:
      'Cuando el motivo del freno esté resuelto, reactivar desde Configuración.',
  }),
  kill_switch_activated: ENTRY({
    label: 'Freno de seguridad activado (cuenta)',
    description:
      'Se congeló la moderación automática de esta cuenta. El bot no responde nada hasta que se reactive.',
    suggestedAction:
      'Cuando el motivo del freno esté resuelto, reactivar desde Configuración.',
  }),

  'master_kill_switch.activated': ENTRY({
    label: 'Freno maestro activado',
    description:
      'Toda la moderación automática del Community Manager está congelada, para todos los clientes a la vez.',
    suggestedAction:
      'Solo desactivar después de confirmar con el directorio que el incidente está resuelto.',
  }),

  bot_stalled: ENTRY({
    label: 'El bot dejó de responder',
    description:
      'El motor de moderación no procesa mensajes desde hace varios minutos y hay pendientes acumulados.',
    suggestedAction:
      'Avisar a soporte técnico. Mientras tanto, considerar takeover humano del Inbox.',
  }),

  'test.notification_check': ENTRY({
    label: 'Prueba de notificaciones',
    description:
      'Esta alerta se disparó a propósito para verificar que los avisos llegan por email y Discord.',
    suggestedAction:
      'Si recibiste el aviso por los canales configurados, la cadena funciona. Después ackeala.',
  }),
}

/**
 * Devuelve la metadata humana de un kind. Si no está mapeado, cae a un default
 * que muestra el kind crudo pero no rompe la UI.
 */
export function getAlertKindMeta(kind: string): AlertKindMeta {
  const meta = ALERT_KINDS[kind]
  if (meta) return meta
  return {
    label: kind,
    description:
      'Tipo de alerta sin descripción cargada todavía. Avisar al equipo técnico para que la documente.',
  }
}

/**
 * Heurística para decidir si `payload.info` que vino del backend ya es
 * texto entendible por un humano, o si es ruido (placeholder de seed, JSON
 * crudo, mensaje vacío). Si no es humano, conviene usar el `description`
 * por defecto del kind.
 */
export function looksHuman(message: string | null | undefined): boolean {
  if (!message) return false
  const trimmed = message.trim()
  if (!trimmed) return false
  // Mensajes de seed obvios
  if (/^alerta seed/i.test(trimmed)) return false
  // Si empieza con { o [ asumimos JSON crudo
  if (/^[{[]/.test(trimmed)) return false
  // Mensajes muy cortos sin espacios huelen a slug técnico
  if (trimmed.length < 15 && !/\s/.test(trimmed)) return false
  return true
}
